// Labels and status metadata for Fördermittel cases. Programme, measure and checklist texts come
// from the rules data (German, as in the official programmes); everything else goes through t().

export const STATUS_COLORS = {
  advised: "default",
  contract: "cyan",
  applied: "blue",
  approved: "geekblue",
  in_progress: "processing",
  completed: "purple",
  proof_submitted: "gold",
  paid_out: "success",
  certificate_issued: "success",
  closed: "default",
  rejected: "error",
  cancelled: "default"
};

export const TERMINAL = ["rejected", "cancelled"];

export const statusLabels = t => ({
  advised: t("Advised"),
  contract: t("Contract signed"),
  applied: t("Application filed"),
  approved: t("Approved"),
  in_progress: t("Work in progress"),
  completed: t("Work completed"),
  proof_submitted: t("Proof submitted"),
  paid_out: t("Paid out"),
  certificate_issued: t("Certificate issued"),
  closed: t("Closed"),
  rejected: t("Rejected"),
  cancelled: t("Cancelled")
});

export const providerLabels = t => ({
  customer: t("Customer"),
  contractor: t("Our company"),
  expert: t("Energy expert"),
  agency: t("Funding agency")
});

export const phaseLabels = t => ({
  before_contract: t("Before the contract"),
  before_application: t("Before the application"),
  after_application: t("After the application"),
  execution: t("Execution"),
  after_completion: t("After completion")
});

export const documentLabels = t => ({
  contractor_declaration: t("Contractor declaration"),
  certificate_35c: t("§ 35c certificate"),
  cost_breakdown: t("Cost breakdown per measure"),
  clause: t("Funding clause for the contract")
});

export const costTypeLabels = t => ({
  labour: t("Labour"),
  material: t("Material"),
  other: t("Other")
});

/**
 * The fields a status asks for (dotted paths into the case) and how to enter them. Mirrors
 * REQUIRED_FIELDS in utils/funding/calc.js; optional extras are entered at the same step.
 */
export const STATUS_FIELDS = {
  contract: [
    { path: "contract.signedAt", type: "date", required: true },
    {
      path: "contract.condition",
      type: "condition",
      required: "conditionRequired",
      when: "conditionRequired"
    },
    { path: "contract.plannedDate", type: "month", required: false, when: "conditionRequired" }
  ],
  applied: [
    { path: "application.submittedAt", type: "date", required: true },
    { path: "application.number", type: "text", required: true },
    { path: "application.confirmationId", type: "text", required: false, when: "grant" }
  ],
  approved: [
    { path: "approval.date", type: "date", required: true },
    { path: "approval.amountCents", type: "money", required: false },
    { path: "approval.reference", type: "text", required: false }
  ],
  in_progress: [{ path: "execution.startedAt", type: "date", required: true }],
  completed: [{ path: "execution.completedAt", type: "date", required: true }],
  proof_submitted: [{ path: "proof.submittedAt", type: "date", required: true }],
  paid_out: [
    { path: "payout.date", type: "date", required: true },
    { path: "payout.amountCents", type: "money", required: true }
  ],
  certificate_issued: [{ path: "certificate.issuedAt", type: "date", required: true }]
};

export const fieldLabels = t => ({
  "contract.signedAt": t("Contract signed on"),
  "contract.condition": t("Funding condition in the contract"),
  "contract.plannedDate": t("Planned implementation (month)"),
  "application.submittedAt": t("Application filed on"),
  "application.number": t("Application number"),
  "application.confirmationId": t("BzA / TPB ID"),
  "approval.date": t("Approval date"),
  "approval.amountCents": t("Approved amount"),
  "approval.reference": t("Approval reference"),
  "execution.startedAt": t("Work started on"),
  "execution.completedAt": t("Work completed on"),
  "proof.submittedAt": t("Proof submitted on"),
  "payout.date": t("Paid out on"),
  "payout.amountCents": t("Amount paid out"),
  "certificate.issuedAt": t("Certificate issued on")
});

export const conditionLabels = t => ({
  suspensive: t("Suspensive condition (aufschiebend)"),
  resolutive: t("Resolutive condition (auflösend)")
});

export const getPath = (object, path) =>
  path.split(".").reduce((value, key) => (value == null ? undefined : value[key]), object);

/** Sets a dotted path on a copy of `object`. */
export const setPath = (object, path, value) => {
  const [head, ...rest] = path.split(".");
  if (!rest.length) return { ...object, [head]: value };
  return { ...object, [head]: setPath(object?.[head] || {}, rest.join("."), value) };
};
